/**
 * Environment / configuration error boundary (APP-001 §20, §33 invariant 20).
 *
 * `loadConfig()` throws synchronously if any required env var is missing or
 * invalid. This boundary renders a clear, safe message instead of a blank
 * page or a stack trace. No secrets are ever shown.
 */
import type { ReactNode } from 'react';

export function EnvErrorBoundary({ error, children }: { error: Error | null; children: ReactNode }) {
  if (!error) return <>{children}</>;
  return (
    <div role="alert" className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border border-destructive/50 bg-surface p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-foreground">Configuration error</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The dashboard could not start because its configuration is incomplete or invalid. No
          sensitive details are shown here.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-md bg-surface-muted p-3 font-mono text-xs text-foreground">
          {error.message}
        </pre>
        <p className="mt-4 text-xs text-muted-foreground">
          Check the <code>VITE_*</code> environment variables and redeploy. See the APP-001
          documentation for the required set.
        </p>
      </div>
    </div>
  );
}
